// @ts-expect-error: Package doesn't have types.
import LintModule from "bpmn-js-bpmnlint";
// @ts-expect-error: Package doesn't have types.
import BpmnColorPickerModule from "bpmn-js-color-picker/colors";
import {
    BpmnPropertiesPanelModule,
    BpmnPropertiesProviderModule,
} from "bpmn-js-properties-panel";
// @ts-expect-error: Package doesn't have types.
import TokenSimulationModule from "bpmn-js-token-simulation";
import type { BaseViewerOptions } from "bpmn-js/lib/BaseViewer";
import type BaseViewer from "bpmn-js/lib/BaseViewer";
import BpmnModeler from "bpmn-js/lib/Modeler";
import { getNewShapePosition } from "bpmn-js/lib/features/auto-place/BpmnAutoPlaceUtil";
import ModelingModule from "bpmn-js/lib/features/modeling";
import type ElementFactory from "bpmn-js/lib/features/modeling/ElementFactory";
import type Modeling from "bpmn-js/lib/features/modeling/Modeling";
import type {
    Element as BpmnElement,
    Connection,
    Moddle,
    Parent,
    Shape,
} from "bpmn-js/lib/model/Types";
import { getBusinessObject, is } from "bpmn-js/lib/util/ModelUtil";
// @ts-expect-error: Package doesn't have types.
import GridModule from "diagram-js-grid";
import type Canvas from "diagram-js/lib/core/Canvas";
import ElementRegistry from "diagram-js/lib/core/ElementRegistry";
import type EventBus from "diagram-js/lib/core/EventBus";
import type Clipboard from "diagram-js/lib/features/clipboard/Clipboard";
import type ContextPad from "diagram-js/lib/features/context-pad/ContextPad";
import type Keyboard from "diagram-js/lib/features/keyboard/Keyboard";
import Overlays from "diagram-js/lib/features/overlays/Overlays";
import type DiagramSelection from "diagram-js/lib/features/selection/Selection";
import { asTRBL, getMid } from "diagram-js/lib/layout/LayoutUtil";
import type { ElementLike } from "diagram-js/lib/model/Types";
import type ZoomScroll from "diagram-js/lib/navigation/zoomscroll/ZoomScroll";
import { Point } from "diagram-js/lib/util/Types";
import type { RouteLocationRaw } from "vue-router";

// EntityType comes from the generated enum rather than the entity store: this
// module is imported by store-less unit tests, and the store's import graph
// reaches firebase.ts, which throws at import time without Firebase env vars.
import { EntityType, ProcessElementType } from "@/lib/openapi/gen";
import { withThreadRevision } from "@/lib/thread-revision-link";

import lintConfig from "./.bpmnlintrc.js";
import CustomDirectEditingModule from "./custom-modules";
import StekzCustomElements from "./custom-modules/custom-elements";
import createStekzRendererModule from "./custom-modules/stekz-renderer";
import StekzModdle from "./stekz-moddle.json";

export const namespace = "http://www.omg.org/spec/BPMN/20100524/MODEL";
export const MAIN_PROCESS_ID = "Process_1";
// Hardcoded to be 120 in lib/features/modeling/cmd/AddLaneHandler.js
const LANE_HEIGHT = 160;

interface BaseModules {
    selection: DiagramSelection;
    elementRegistry: ElementRegistry;
    canvas: Canvas;
    eventBus: EventBus;
    modeling?: Modeling;
    moddle?: Moddle;
    elementFactory?: ElementFactory;
    zoomScroll?: ZoomScroll;
    propertiesPanel?: BpmnPropertiesPanelModule;
    contextPad?: ContextPad;
    clipboard?: Clipboard;
    keyboard?: Keyboard;
    overlays: Overlays;
}

/**
 * Helper type to enforce modules for Viewer/Modeler.
 */
export type NavigatedViewer<Modules extends BaseModules = BaseModules> =
    BaseViewer<{ zoomScroll: ZoomScroll } & Modules>;

export type Modeler<Modules extends BaseModules = BaseModules> =
    | BaseViewer<
          {
              modeling: Modeling;
              elementFactory: ElementFactory;
          } & Modules
      >
    | (BaseViewer<
          {
              modeling: Modeling;
              elementFactory: ElementFactory;
          } & Modules
      > &
          BpmnModeler);

export const createBpmnModeler = (
    propertiesPanelElement?: HTMLElement,
    {
        hideLaneNames = false,
    }: {
        hideLaneNames?: boolean;
    } = {}
) => {
    const additionalModules: BaseViewerOptions["additionalModules"] = [
        ModelingModule,
        TokenSimulationModule,
        LintModule,
        BpmnColorPickerModule,
        GridModule,
        createStekzRendererModule(hideLaneNames),
    ];

    if (propertiesPanelElement) {
        additionalModules.push(
            BpmnPropertiesPanelModule,
            BpmnPropertiesProviderModule
        );
    }

    additionalModules.push(StekzCustomElements, CustomDirectEditingModule);

    return new BpmnModeler({
        additionalModules,
        moddleExtensions: {
            stekz: StekzModdle,
        },
        linting: {
            bpmnlint: lintConfig,
            active: false,
        },
        zoomScroll: {
            scale: 0.3,
            enabled: false,
        },
    });
};
/**
 * Use the base.bpmn to create a proper BPMN for the process.
 */
export async function initBpmn(
    requiredLanes: { name: string; roleId?: string }[]
) {
    const modeler = createBpmnModeler();
    await modeler.createDiagram();

    await addLanes(modeler, requiredLanes);

    const participants = await getParticipants(modeler);

    const startEvent = await addStartEvent(modeler, participants[0] as Parent);
    await addShapeAfter(modeler, startEvent, { type: "bpmn:Task" });

    return (await modeler.saveXML()).xml;
}

/**
 * Add a StartEvent to given parent.
 */
export async function addStartEvent(modeler: Modeler, parent: Parent) {
    const elementFactory = modeler.get("elementFactory");
    const modeling = modeler.get("modeling");

    const startEvent = elementFactory.createShape({
        type: "bpmn:StartEvent",
    });
    const parentBounds = parent.di.bounds;
    let y = 0;
    if (is(parent, "bpmn:Lane")) {
        y = getMid(parentBounds).y;
    }

    modeling.createShape(startEvent, { x: -200, y }, parent);
    startEvent.parent = parent;
    return startEvent;
}

/**
 * Check if a shape is overlapping with any other shape.
 */
function isOverlapping(
    position: Point,
    newShape: Shape,
    elements: ElementLike[]
): boolean {
    return elements.some((existingShape) => {
        try {
            if (
                existingShape.type != "bpmn:Task" &&
                existingShape.type != "bpmn:ExclusiveGateway"
            )
                return false;
            const existingTRBL = asTRBL(existingShape as Shape);
            return (
                position.x + newShape.width > existingTRBL.left &&
                position.x - newShape.width < existingTRBL.right &&
                position.y + newShape.height > existingTRBL.top &&
                position.y - newShape.height < existingTRBL.bottom
            );
        } catch {
            return false;
        }
    });
}

/**
 * Add a shape after another shape, connected with a SequenceFlow.
 */
export async function addShapeAfter(
    modeler: Modeler,
    source: Shape,
    shapeOpts: Partial<Shape>,
    label?: string, // Optional name argument
    lane?: Parent,
    connectionLabel?: string
): Promise<[Connection, Shape]> {
    const elementFactory = modeler.get("elementFactory");
    const modeling = modeler.get("modeling");

    const parent: Parent = lane || (source.parent as Parent);
    const newShape = elementFactory.createShape(shapeOpts);
    const sourceCopy = elementFactory.createShape({
        type: source.type,
        x: source.x,
        y: source.y,
        width: source.width,
        height: source.height,
    });
    /**
     * Act like the source is in the new lane.
     * This is just so the correct position for the
     * new element can be calculated easily.
     */
    if (lane) {
        sourceCopy.y = getMid(lane).y - sourceCopy.height / 2;
    }

    /**
     * Increase the distance for appending to a gateway by moving the fake source.
     */
    if (is(source, "bpmn:Gateway")) {
        sourceCopy.x = sourceCopy.x + 60;
    }

    // NOTE: In some cases this doesn't work.
    const newShapePos = getNewShapePosition(
        sourceCopy,
        newShape,
        // @ts-expect-error: If we actually pass the elementRegistry here, it breaks
        null
    );
    const elementRegistry = modeler.get("elementRegistry");
    const allShapes = elementRegistry.getAll();
    while (isOverlapping(newShapePos, newShape, allShapes)) {
        newShapePos.y += 60;
    }

    /**
     * Increase lane size if not in lane anymore.
     */
    if (lane) {
        const laneTRBL = asTRBL(lane as Shape);
        if (newShapePos.y > laneTRBL.bottom) {
            const newBounds = { ...lane.di.bounds };
            newBounds.height += newShapePos.y - laneTRBL.top;
            modeling.resizeLane(lane as Shape, newBounds, false);
        }
    }

    newShape.parent = parent;
    modeling.createShape(newShape, newShapePos, parent);

    if (label) {
        modeling.updateLabel(newShape, label);
    }

    const sequenceFlow = await createConnection(
        elementFactory,
        modeling,
        source,
        newShape,
        parent,
        connectionLabel
    );
    return [sequenceFlow, newShape];
}

/**
 * Create a connection between two shapes.
 */
async function createConnection(
    elementFactory: ElementFactory,
    modeling: Modeling,
    source: Shape,
    target: Shape,
    parent: Parent,
    connectionLabel?: string
): Promise<Connection> {
    const attrs: Partial<Connection> = {
        type: "bpmn:SequenceFlow",
        source: source,
        target: target,
    };
    const sequenceFlow = elementFactory.createConnection(attrs);
    const connection = modeling.createConnection(
        source,
        target,
        sequenceFlow,
        parent
    );
    const CONNECTION_OFFSET_Y = -60;

    /**
     * Handle backwards connection by moving it to the top.
     */
    if (source.x > target.x) {
        const lastWaypoint = { ...connection.waypoints.at(-1)! };
        const secondToLastWaypoint = { ...connection.waypoints.at(-2)! };
        let newWaypoints: Point[];

        if (connection.waypoints.length < 2) {
            console.warn("Got connection with less than 2 waypoints.");
            return connection;
        }

        const newSegmentY = lastWaypoint.y + CONNECTION_OFFSET_Y;

        // Doesn't have segments, yet.
        if (connection.waypoints.length === 2) {
            // Create new waypoints in between and keep the originals.
            newWaypoints = [
                connection.waypoints[0],
                { ...secondToLastWaypoint, y: newSegmentY },
                { x: getMid(target).x, y: newSegmentY },
                { x: getMid(target).x, y: target.y },
            ];
        } else {
            // Createn new waypoints, replacing the second to last waypoint.
            newWaypoints = [
                ...connection.waypoints.slice(0, -2),
                { ...secondToLastWaypoint, y: newSegmentY },
                { x: getMid(target).x, y: newSegmentY },
                { x: getMid(target).x, y: target.y },
            ];
        }

        modeling.updateWaypoints(connection, newWaypoints);
    }

    if (connectionLabel) {
        modeling.updateLabel(connection, connectionLabel);
    }
    return connection;
}

/*
 *Get the target lane for a given lane name.
 */
export async function getLane(
    modeler: Modeler,
    laneName: string
): Promise<Parent> {
    const mainParticipant = await getMainParticipant(modeler);
    if (!mainParticipant) {
        throw new Error("Could not find the main participant.");
    }

    const lane = mainParticipant.children.find(
        (child: Shape) =>
            is(child, "bpmn:Lane") && getBusinessObject(child).name === laneName
    );

    if (!lane) {
        throw new Error(`Could not find the lane "${laneName}".`);
    }

    return lane;
}

// Get the root element from the registry. This root element describes the
// 'top level' element, which is typically either a simple process or a
// collaboration in case it contains pools.
export function getRootElement(elementRegistry: ElementRegistry): Parent {
    const root = elementRegistry.find((element) => {
        const businessObject = getBusinessObject(element);
        return (
            businessObject &&
            !businessObject.parent &&
            (businessObject.$type === "bpmn:Process" ||
                businessObject.$type === "bpmn:Collaboration")
        );
    });
    if (!root) {
        throw Error("No root element found.");
    }
    return root as Parent;
}

export async function doesLaneExist(modeler: Modeler, laneName: string) {
    const currentLanes = (await getLaneElements(modeler)).map(
        (lane) => lane.name
    );
    return currentLanes.includes(laneName);
}

/**
 * Modifies a bpmn file by turning a root level process into a collaboration,
 * adds a participant to describe a certain domain, and then adds lanes per
 * role that is given. Typically these roles are the roles that are available
 * within the domain.
 */
export async function addLane(
    modeler: Modeler,
    laneName: string,
    roleId?: string
): Promise<BpmnElement> {
    const laneAlreadyExists = await doesLaneExist(modeler, laneName);
    if (laneAlreadyExists) {
        throw new Error("The given lane already exist in the BPMN file.");
    }

    const elementRegistry = modeler.get("elementRegistry");
    const participantShape = elementRegistry.filter(
        (el) => el.type === "bpmn:Participant"
    )[0] as Shape | undefined;
    if (!participantShape) {
        throw new Error("Could not find the participant shape.");
    }

    const hasLanes = participantShape.children.some(
        (child) => child.type === "bpmn:Lane"
    );
    const modeling = modeler.get("modeling");

    let lane: BpmnElement | undefined;
    if (!hasLanes) {
        modeling.splitLane(participantShape, 1);
        lane = participantShape.children
            .toReversed()
            .find((child) => child.type === "bpmn:Lane") as BpmnElement;
    } else {
        lane = modeling.addLane(participantShape, "bottom");
    }
    if (!lane) {
        throw new Error(`Something went wrong when creating lane: ${laneName}`);
    }

    modeling.updateProperties(lane as BpmnElement, {
        name: laneName,
        roleId: roleId,
    });
    modeling.resizeLane(lane as Shape, {
        ...lane.di.bounds,
        height: LANE_HEIGHT,
    });
    return lane;
}

export async function addLanes(
    modeler: Modeler,
    requiredLanes: { name: string; roleId?: string }[]
): Promise<BpmnElement[]> {
    if (!requiredLanes || requiredLanes.length === 0) {
        throw new Error(
            "Cannot create a BPMN diagram without at least one lane. No roles provided."
        );
    }

    const modeling = modeler.get("modeling");
    const root = modeling.makeCollaboration();
    const elementFactory = modeler.get("elementFactory");
    const participantShape = elementFactory.createParticipantShape({
        type: "bpmn:Participant",
        height: LANE_HEIGHT,
    });
    modeling.createShape(participantShape, { x: 0, y: 0 }, root);

    const lanes: BpmnElement[] = [];
    for (const lane of requiredLanes) {
        const createdLane = await addLane(modeler, lane.name, lane.roleId);
        lanes.push(createdLane);
    }
    return lanes;
}

// When a bpmn file has a collaboration as a root element, extract the
// referred to processes for every participant in that collaboration.
export async function getProcessesFromCollaboration(
    modeler: Modeler
): Promise<Element[]> {
    const participants = await getParticipants(modeler);
    const processes: Element[] = [];
    participants.forEach((participant) => {
        const process = participant.businessObject.processRef;
        if (process) {
            processes.push(process);
        }
    });

    return processes;
}

export async function getParticipants(
    modeler: Modeler
): Promise<ElementLike[]> {
    const elementRegistry = modeler.get("elementRegistry");

    const rootElement = getRootElement(elementRegistry) as Shape;
    if (!is(getBusinessObject(rootElement), "bpmn:Collaboration")) {
        return [];
    }

    return rootElement.children.filter(
        (child) => child.type === "bpmn:Participant"
    );
}

export async function getLaneElements(
    modeler: Modeler
): Promise<ElementLike[]> {
    const processes = await getProcessesFromCollaboration(modeler);
    if (processes.length === 0) {
        return [];
    }

    const lanes: Shape[] = [];
    processes.forEach((process) => {
        if (!("laneSets" in process)) {
            return;
        }

        (process.laneSets as Shape[]).forEach((laneSet) => {
            if (!("lanes" in laneSet)) {
                return;
            }

            (laneSet.lanes as Shape[]).forEach((lane) => {
                lanes.push(lane);
            });
        });
    });

    return lanes;
}

/**
 * Get the dimensions of the BPMN diagram based on the elements.
 */
export function getBPMNDimensions(viewer: NavigatedViewer) {
    const canvas = viewer.get("canvas");
    if (!("_rootElement" in canvas) || !canvas._rootElement) {
        return;
    }

    const rootElement = canvas.getRootElement();
    const bpmnElements = rootElement.children;

    // Calculate min and max values of X and Y among all elements in the diagram
    let minX = Infinity,
        maxX = -Infinity,
        minY = Infinity,
        maxY = -Infinity;
    for (const element of bpmnElements) {
        if (element.di.bounds) {
            minX = Math.min(minX, element.di.bounds.x);
            maxX = Math.max(
                maxX,
                element.di.bounds.x + element.di.bounds.width
            );
            minY = Math.min(minY, element.di.bounds.y);
            maxY = Math.max(
                maxY,
                element.di.bounds.y + element.di.bounds.height
            );
        }
        if (element.di.waypoint) {
            for (const waypoint of element.di.waypoint) {
                minX = Math.min(minX, waypoint.x);
                maxX = Math.max(maxX, waypoint.x);
                minY = Math.min(minY, waypoint.y);
                maxY = Math.max(maxY, waypoint.y);
            }
        }
    }

    // Calculate the central position
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    return { width: maxX - minX, height: maxY - minY, centerX, centerY };
}

/**
 * Returns whether an element is a SubProcess element usable as root.
 *
 * Does NOT return true for visual elements.
 */
export const elementIsSubProcess = (element: ElementLike): boolean => {
    return is(element, "bpmn:SubProcess") && Object.hasOwn(element, "layer");
};

/**
 * Returns whether an element is a SubProcess element activity Shape.
 *
 * Does NOT return true for a root element.
 */
export const elementIsSubProcessShape = (element: ElementLike): boolean => {
    return is(element, "bpmn:SubProcess") && !Object.hasOwn(element, "layer");
};

export const elementIsCallActivity = (element: ElementLike): boolean => {
    return is(element, "bpmn:CallActivity");
};

export const elementIsPool = (element: ElementLike): boolean => {
    return is(element, "bpmn:Participant");
};

export const elementIsCollapsedPool = (element: ElementLike): boolean => {
    return (
        is(element, "bpmn:Participant") &&
        (element.collapsed || !getBusinessObject(element).processRef)
    );
};

export const elementIsLane = (element: ElementLike): boolean => {
    return is(element, "bpmn:Lane");
};

export const elementIsTask = (element: ElementLike): boolean => {
    return is(element, "bpmn:Task");
};

/**
 * Check whether the given element is navigatable (i.e. it has a link to
 * another entity or is a subprocess that can be drilled into).
 */
export const elementIsNavigatable = (element: ElementLike): boolean => {
    const bo = getBusinessObject(element);

    const isStandardNavigational =
        (elementIsTask(element) ||
            elementIsEvent(element as BpmnElement) ||
            elementIsGateway(element as BpmnElement)) &&
        bo.processElementId;

    const isLinkedEntity =
        (elementIsCallActivity(element) || elementIsCollapsedPool(element)) &&
        (bo.processId || bo.calledElementId);

    return (
        elementIsSubProcessShape(element) ||
        isStandardNavigational ||
        isLinkedEntity
    );
};

/**
 * Get the router route for a navigatable BPMN element.
 */
export const getNavigationRoute = (
    element: ElementLike,
    orgName: string,
    stekName: string
): RouteLocationRaw | undefined => {
    const bo = getBusinessObject(element);
    const commonParams = {
        orgName,
        stekName,
    };

    if (bo.processElementId) {
        return withThreadRevision({
            name: "processElementPage",
            params: {
                ...commonParams,
                elementId: bo.processElementId,
                elementType: elementType(element as BpmnElement),
            },
        });
    }

    const type = bo.calledElementType as EntityType;
    const id = bo.calledElementId || bo.processId;

    if (type === EntityType.DOMAIN) {
        return withThreadRevision({
            name: "domainPage",
            params: { ...commonParams, domainId: id },
        });
    } else if (type === EntityType.PRODUCT) {
        return withThreadRevision({
            name: "productPage",
            params: { ...commonParams, productId: id },
        });
    } else if (type === EntityType.PROCESS || elementIsCallActivity(element)) {
        return withThreadRevision({
            name: "processPage",
            params: { ...commonParams, processId: id },
        });
    }

    return undefined;
};

/**
 * Check whether the given bpmnElementType (e.g. bpmn:Task) is of an
 * element type that can contain stekz content (i.e. have files
 * associated to it)
 */
export const canHaveContent = (element: ElementLike): boolean => {
    if (!element) {
        return false;
    }

    const contentTypes = ["Task", "Event", "Gateway"];
    return (
        contentTypes.some((type) => element.type.includes(type)) &&
        !elementIsSubProcess(element)
    );
};

export const elementIsEvent = (element: BpmnElement): boolean => {
    return is(element, "bpmn:Event");
};

export const elementIsGateway = (element: BpmnElement): boolean => {
    return is(element, "bpmn:Gateway");
};

export const elementIsMessageFlow = (element: BpmnElement): boolean => {
    return is(element, "bpmn:MessageFlow");
};

export const elementIsSequenceFlow = (element: BpmnElement): boolean => {
    return is(element, "bpmn:SequenceFlow");
};

/**
 * Get the BPMN element type as ProcessElementType enum.
 */
export const elementType = (
    element: BpmnElement
): ProcessElementType | undefined => {
    if (elementIsTask(element)) {
        return ProcessElementType.TASK;
    } else if (elementIsEvent(element)) {
        return ProcessElementType.EVENT;
    } else if (elementIsGateway(element)) {
        return ProcessElementType.GATEWAY;
    } else if (
        elementIsMessageFlow(element) ||
        elementIsSequenceFlow(element)
    ) {
        return ProcessElementType.FLOW;
    }
};
/**
 * A factory function that returns a reviver to be
 * used with JSON#parse to reinstantiate moddle instances.
 */
export function createReviver(moddle: Moddle) {
    var elCache: Record<string, BpmnElement> = {};

    /**
     * The actual reviewer that creates model instances
     * for elements with a $type attribute.
     *
     * Elements with ids will be re-used, if already
     * created.
     */
    return function (_: string, object: BpmnElement) {
        if (typeof object === "object" && typeof object.$type === "string") {
            var objectId = object.id;

            if (objectId && elCache[objectId]) {
                return elCache[objectId];
            }

            var type = object.$type;
            var attrs = Object.assign({}, object);

            delete attrs.$type;

            var newEl = moddle.create(type, attrs);

            if (objectId) {
                elCache[objectId] = newEl;
            }

            return newEl;
        }

        return object;
    };
}

/**
 * Checks whether the given element is the main or only participant.
 */
export const isProcessParticipant = (
    element: ElementLike,
    processId: string
) => {
    if (getBusinessObject(element).processId === processId) {
        return true;
    }
    return false;
};

/**
 * Get the main participant from the modeler.
 */
export const getMainParticipant = async (
    modeler: Modeler,
    processId?: string
) => {
    const participants = await getParticipants(modeler);
    // A lone participant is only assumed to be the main one if it isn't a
    // closed pool: a diagram can consist of just a closed pool linking out
    // to another entity, with no participant representing its own flow.
    if (participants.length === 1 && !elementIsCollapsedPool(participants[0])) {
        return participants[0];
    }
    if (!processId) {
        return;
    }
    return participants.find((p) => isProcessParticipant(p, processId));
};
